import { Normalizer } from 'utils/normalizers/types';
import {
  ArticleApiResponse,
  DailyPicksApiResponse,
  ExploreApiResponse,
  ExploreSectionApiResponse,
  ExploreSectionItemApiResponse,
  ExploreSourceTypeApiResponse,
  ExploreSourceTypeSectionApiResponse,
  InsightApiResponse,
  isExploreSourceTypeSectionSourceItemApiResponse,
  isExploreSourceTypeSectionUserItemApiResponse,
  UserDataApi,
} from 'api/api.types';
import {
  DailyPicksSection,
  DailyPicksSectionType,
  ExploreSection,
  ExploreSectionHashtagHighlightItem,
  ExploreSectionHashtagItem,
  ExploreSectionItem,
  ExploreSourceTypeData,
  ExploreSourceTypeSection,
} from 'types';
import {
  DAILY_PICKS_ID_PREFIX,
  getExploreSectionItemIllustration,
  getExploreSectionItemType,
  getHashtagNameFromSectionItemId,
  getInitialSectionFromExploreDestinationPath,
} from 'utils/explore.utils';
import { normalizeIdea, normalizeSource } from 'utils/normalizers/source';
import { normalizeUserData } from 'utils/normalizers/profile';
import { getSourceTypePlural } from 'utils/source.utils';
import { SOURCES_TYPE_ID_TYPE_PREFIX } from 'utils/explore.constants';

export const normalizeExploreSectionItemApiResponse: Normalizer<
  ExploreSectionItemApiResponse,
  ExploreSectionItem
> = res => {
  const sectionType = getExploreSectionItemType(res.id);

  // The API sends `sources-by-type-article` instead of `sources-by-type-articles` as the id for the articles categ
  // Change it there in the normalization so that the components in the page can expect that the source type is always plural
  let correctedId = res.id;
  if (correctedId === `${SOURCES_TYPE_ID_TYPE_PREFIX}article`) {
    correctedId = `${SOURCES_TYPE_ID_TYPE_PREFIX}articles`;
  }

  const exploreBasicSection: ExploreSectionItem = {
    id: correctedId,
    title: res.title,
    subtitle: res.subtitle ?? undefined,
    illustration: getExploreSectionItemIllustration({
      id: correctedId,
      type: sectionType,
    }),
    type: sectionType,
  };

  if (sectionType === 'hashtag') {
    return {
      ...exploreBasicSection,
      hashtagName: getHashtagNameFromSectionItemId(exploreBasicSection.id),
    } as ExploreSectionHashtagItem;
  } else if (sectionType === 'highlight-hashtag') {
    return {
      ...exploreBasicSection,
      hashtagName: getHashtagNameFromSectionItemId(exploreBasicSection.id),
      initialSection: getInitialSectionFromExploreDestinationPath(
        res.destination_path,
      ),
    } as ExploreSectionHashtagHighlightItem;
  }

  return exploreBasicSection;
};

export const normalizeExploreSectionApiResponse: Normalizer<
  ExploreSectionApiResponse,
  ExploreSection
> = res => ({
  id: res.id,
  title: res.title,
  items: res.objects.map(el => normalizeExploreSectionItemApiResponse(el)),
});

export const normalizeExploreApiResponse: Normalizer<
  ExploreApiResponse,
  ExploreSection[]
> = res =>
  mutateExploreResponse(res).objects.map(el =>
    normalizeExploreSectionApiResponse(el),
  );

export const mutateExploreResponse: (
  exploreApiResponse: ExploreApiResponse,
) => ExploreApiResponse = exploreApiResponse => {
  return {
    ...exploreApiResponse,
    objects: [
      {
        ...exploreApiResponse.objects[0],
        objects: exploreApiResponse.objects[0]?.objects.concat(
          exploreApiResponse.objects[1]?.objects ?? [],
        ),
      },
      exploreApiResponse.objects[2],
    ],
  };
};

export const normalizeExploreSourceTypeSectionApiResponse: Normalizer<
  ExploreSourceTypeSectionApiResponse,
  ExploreSourceTypeSection
> = res => {
  let normalizedObjects: any = [];
  const itemType = res.objects[0]?._object_type;

  if (itemType === 'article') {
    normalizedObjects = res.objects
      // Keep only the articles
      // This filtering is done as type check. If the above if is true, all items should be articles
      .filter(obj => isExploreSourceTypeSectionSourceItemApiResponse(obj))
      .map(obj => normalizeSource(obj as ArticleApiResponse));
  } else if (itemType === 'user') {
    normalizedObjects = res.objects
      // Keep only the users
      // This filtering is done as type check. If the above if is true, all items should be users
      .filter(obj => isExploreSourceTypeSectionUserItemApiResponse(obj))
      .map(obj => normalizeUserData(obj as UserDataApi));
  }

  // Trim the /api/v2 prefix
  let normalizedLink = res.link.substring(7);

  // Now get the source type from the end and turn it from singular to plural
  const linkTokens = normalizedLink.split('/');
  // The last token is an empty string since there is a trailing /, so the source type is the one before
  linkTokens[linkTokens.length - 2] = getSourceTypePlural(
    linkTokens[linkTokens.length - 2],
  );

  normalizedLink = linkTokens.join('/');
  return {
    id: res.id,
    itemType,
    name: res.name,
    link: normalizedLink,
    items: normalizedObjects,
  };
};

export const normalizeExploreSourceTypeApiResponse: Normalizer<
  ExploreSourceTypeApiResponse,
  ExploreSourceTypeData
> = res => ({
  ideaCount: res.total_ideas,
  sections: res.objects.map(el =>
    normalizeExploreSourceTypeSectionApiResponse(el),
  ),
});

export const normalizeDailyPicksApiResponse: Normalizer<
  DailyPicksApiResponse,
  DailyPicksSection[]
> = res => {
  return res.objects.map(sectionObject => {
    let sectionType: DailyPicksSectionType;
    switch (sectionObject.id.substring(DAILY_PICKS_ID_PREFIX.length)) {
      case 'botd': {
        sectionType = DailyPicksSectionType.BOOK_OF_THE_DAY;
        break;
      }
      case 'botd-history': {
        sectionType = DailyPicksSectionType.BOOK_OF_THE_DAY_HISTORY;
        break;
      }
      case 'qotd': {
        sectionType = DailyPicksSectionType.QUOTE_OF_THE_DAY;
        break;
      }
      case 'qotd-history': {
        sectionType = DailyPicksSectionType.QUOTE_OF_THE_DAY_HISTORY;
        break;
      }
      case 'todays-pick': {
        sectionType = DailyPicksSectionType.PICKS;
        break;
      }
      case 'previous-picks': {
        sectionType = DailyPicksSectionType.PICKS_HISTORY;
        break;
      }
      default: {
        throw Error('Unsupported daily pick section');
      }
    }
    return {
      id: sectionObject.id,
      name: sectionObject.name,
      // Remove /api/v2
      // The page link is with - instead of _
      link: sectionObject.link
        ?.substring(7)
        .replace('daily_picks', 'daily-picks'),
      sectionType,
      itemType: sectionObject.objects[0].object._object_type,
      items:
        sectionObject.objects[0].object._object_type === 'article'
          ? sectionObject.objects.map(({ object }) =>
              normalizeSource(object as ArticleApiResponse),
            )
          : sectionObject.objects.map(({ object }) =>
              normalizeIdea(object as InsightApiResponse),
            ),
    };
  });
};
